import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { enPack } from "./store";
import type { ExtraBundle, Lang, LocaleBundle, LocalePack, SectionsBundle } from "./types";

const LocaleContext = createContext<LocalePack>(enPack);

// One provider is mounted at a time (the app root). A pack applied before it
// mounts is kept here and picked up by the mount effect.
let mounted: Dispatch<SetStateAction<LocalePack>> | null = null;
let pending: LocalePack | null = null;

/**
 * Switches the whole tree to `pack`. The re-render runs as a transition so it
 * yields to input instead of blocking the main thread. Safe to call before the
 * provider has mounted.
 */
export function applyPack(pack: LocalePack) {
  if (mounted) {
    const set = mounted;
    startTransition(() => set(pack));
  } else {
    pending = pack;
  }
}

export function LocaleProvider({
  initialPack = enPack,
  children,
}: {
  initialPack?: LocalePack;
  children: ReactNode;
}) {
  const [pack, setPack] = useState(initialPack);

  useEffect(() => {
    mounted = setPack;
    if (pending) {
      const next = pending;
      pending = null;
      startTransition(() => setPack(next));
    }
    return () => {
      if (mounted === setPack) mounted = null;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = pack.lang;
    document.documentElement.dir = pack.lang === "ar" ? "rtl" : "ltr";
  }, [pack]);

  return <LocaleContext.Provider value={pack}>{children}</LocaleContext.Provider>;
}

export const usePack = (): LocalePack => useContext(LocaleContext);
export const useLang = (): Lang => usePack().lang;
export const useLocale = (): LocaleBundle => usePack().locale;
export const useSections = (): SectionsBundle => usePack().sections;
export const useExtra = (): ExtraBundle => usePack().extra;
